import type {CSSProperties} from "react";
import {useCallback, useEffect, useMemo, useState} from "react";
import {Modal} from "react-bootstrap";
import {Trans, useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";
import {useDispatch, useSelector} from "react-redux";
import {useOrderFlow} from "../app/providers/OrderFlowProvider";
import CustomModal from "./customModal";
import {formatDatetimeByLocale, formatDateWithTimezoneForApi, parseDateValue} from "../helpers/normalizers";
import {getOrderUiConditions, isDetailedOrderData} from "../helpers/uiConditions";
import {normalizeDialCodeSymbol, normalizePhoneLocalNumber} from "../helpers/phone";
import {buildPickupQuickOptions, PICKUP_QUICK_OFFSETS_MINUTES} from "../helpers/pickupQuickTime";
import {extractTargetOrLastCheckout} from "../helpers/checkouts";
import {getCheckoutPrintErrorMessage, printCheckoutReceipt} from "../helpers/checkoutReceiptPrinting";
import {sendAxiosRequestWithRetry} from "../services/sendAxiosRequestWithRetry";
import {addToFilteredNotificationOrders} from "../store/slices/notificationSlice";
import {toast} from "react-hot-toast";
import {ReadonlyDeliverySummary, ReadonlyPickupSummary} from "./Dashboard/commonComponents/ReadonlyOrderSummaries";
import PickupQuickTimeSelector from "./PickupQuickTimeSelector";
import type { PickupQuickTimeSelectorOption } from "./PickupQuickTimeSelector";
import {useRestaurantDetails} from "../app/providers/RestaurantDetailsContext";
import type {RootState} from "../store";
import { showCustomErrorToast } from "../helpers/toasts";

import "./OrderSocketNotificationModal.scss";


type OrderSocketNotificationModalProps = {
    show?: boolean;
    orderDetails: CashBoxOrderItemData;
    onHide?: () => void;
    backdrop?: true | false | "static";
    enforceFocus?: boolean;
    modalStyle?: CSSProperties;
};

const OrderSocketNotificationModal = ({
    orderDetails,
    onHide,
    backdrop = "static",
    enforceFocus = true,
    modalStyle,
}: OrderSocketNotificationModalProps) => {
    const { i18n, t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const [resolvedOrderDetails, setResolvedOrderDetails] = useState<CashBoxOrderDetails>();
    const [detailsLoading, setDetailsLoading] = useState(false);
    const [actionLoading, setActionLoading] = useState("");
    const [selectedPickupTime, setSelectedPickupTime] = useState("");

    const {
        openOrderByOrderDetails,
        // cancelOrderWithConfirm,
        cancelOrder,
        acceptDeliveryOrPickupOrder,
    } = useOrderFlow();

    const { restaurantDetails } = useRestaurantDetails();

    const {
        currency = "",
        waiter_permissions,
    } = restaurantDetails || {};

    const printMethod = useSelector((state: RootState) => state.appGeneral.printMethod);
    const autoPrint = useSelector((state: RootState) => state.appGeneral.autoPrint);
    const printerSize = useSelector((state: RootState) => state.appGeneral.printerSize);
    const shouldAutoPrint = printMethod !== "browser" && autoPrint;

    const activeOrderDetails = resolvedOrderDetails || orderDetails;
    const detailedActiveOrderDetails = isDetailedOrderData(activeOrderDetails)
        ? activeOrderDetails
        : null;

    const handleClose = useCallback(() => {
        if (typeof onHide === "function") onHide();
    }, [onHide]);

    useEffect(() => {
        let isCancelled = false;

        const fetchFullOrderDetails = async () => {
            setDetailsLoading(true);

            const {
                data: respData,
            } = await sendAxiosRequestWithRetry<CashBoxOrderDetails>({
                method: "get",
                url: "/order/details",
                params: {
                    order_id: orderDetails.id,
                },
            });

            if (!isCancelled && respData) {
                setResolvedOrderDetails(respData);
            }

            if (!isCancelled) {
                setDetailsLoading(false);
            }
        };

        fetchFullOrderDetails();

        return () => {
            isCancelled = true;
        };
    }, [orderDetails?.id]);

    const {
        isPickupOrder: isPickup,
        canRejectOrder,
        needsOrderAcceptance: orderNeedsAcceptance,
    } = getOrderUiConditions(activeOrderDetails, waiter_permissions);

    const orderTypeKey = activeOrderDetails?.order_type;
    const orderTypeText = orderTypeKey
        ? String(t(orderTypeKey, activeOrderDetails?.order_type || "- - -"))
        : "- - -";

    const createdAtText = formatDatetimeByLocale(
        activeOrderDetails?.created_at_timezone,
        i18n,
        { fallback: "- - -" }
    );

    const orderTotalText = activeOrderDetails?.cost ?? "- - -";

    const delivery = detailedActiveOrderDetails?.delivery ?? null;
    const json_data = detailedActiveOrderDetails?.json_data ?? null;
    const itemsOfDetailedOrderData = detailedActiveOrderDetails?.items ?? null;
    const customItems = detailedActiveOrderDetails?.customItems ?? null;
    const pickup_datetime = detailedActiveOrderDetails?.pickup_datetime ?? null;

    const tipAmount = useMemo(() => {
        const tips = detailedActiveOrderDetails?.tips;
        if (!Array.isArray(tips)) return 0;

        return tips.reduce((sum, tipItem) => {
            return sum + (Number(tipItem?.amount) || 0);
        }, 0);
    }, [detailedActiveOrderDetails]);

    const deliveryData = delivery?.delivery_json_data ?? null;
    const deliveryAddressDetails = deliveryData?.receiver_address_details ?? null;

    const pickupEmail = `${json_data?.email || ""}`.trim();
    const pickupDialCodeSymbol = normalizeDialCodeSymbol(
        json_data?.phone_number?.country_symbol || "at",
        "at"
    );
    const pickupPhoneNumber = normalizePhoneLocalNumber(
        json_data?.phone_number?.number || "",
        pickupDialCodeSymbol
    );

    const staticItems = useMemo(() => {
        const orderItems =
            Array.isArray(itemsOfDetailedOrderData)
                ? itemsOfDetailedOrderData :
            Array.isArray(activeOrderDetails?.items)
                ? activeOrderDetails.items
                : [];

        const mappedOrderItems = orderItems.map((item, idx) => {
            const additives = "additives" in item ? item.additives : null;
            const image = "image" in item ? item.image : "";
            const additivesPrice = Array.isArray(additives)
                ? additives.reduce((sum, additive) => sum + (Number(additive?.price) || 0), 0)
                : 0;

            return {
                key: `item-${item?.id ?? idx}`,
                title: item?.title?.trim() || t("untitledItem"),
                count: Number(item?.count) || 0,
                unitPrice: (Number(item?.price) || 0) + additivesPrice,
                image: image || "",
            };
        });

        const mappedCustomItems = (customItems || []).map((item, idx) => ({
            key: `custom-item-${item?.id ?? idx}`,
            title: item?.title?.trim() || t("untitledItem"),
            count: Number(item?.count) || 0,
            unitPrice: Number(item?.price) || 0,
            image: item?.image || "",
        }));

        return [...mappedOrderItems, ...mappedCustomItems];
    }, [activeOrderDetails?.items, itemsOfDetailedOrderData, customItems, t]);

    const pickupBaseDate = useMemo(() => {
        if (!detailedActiveOrderDetails) return null;

        const parsedDate = parseDateValue(pickup_datetime);
        if (parsedDate) {
            return parsedDate;
        }
        else {
            showCustomErrorToast({
                title: t("somethingWentWrong"),
                message: !pickup_datetime
                    ? t("noPickupTimeProvidedForThisOrder")
                    : t("pickupTimeCalculationForThisOrderFailed"),
                opts: { duration: 8000 }
            });
            return null;
        }
    }, [detailedActiveOrderDetails, pickup_datetime, t]);

    const pickupQuickTimeOptions = useMemo(() => {
        if (!pickupBaseDate) return [];

        return buildPickupQuickOptions({
            baseDateObj: pickupBaseDate,
            offsetsMinutes: [0, ...PICKUP_QUICK_OFFSETS_MINUTES],
            mapOption: (option) => {
                const apiValue = formatDateWithTimezoneForApi(option?.dateObj);
                if (!apiValue) return null;

                return {
                    key: `${option.offsetMinutes}-${apiValue}`,
                    label: option.offsetMinutes === 0
                        ? t("pickupTimeInitialShort")
                        : `+${option.offsetMinutes}`,
                    apiValue,
                    displayValue: formatDatetimeByLocale(option.dateObj, i18n, {
                        fallback: "- - -",
                        includeSeconds: false,
                    }),
                };
            },
        });
    }, [pickupBaseDate, t, i18n]);

    useEffect(() => {
        if (!pickupQuickTimeOptions.length) {
            setSelectedPickupTime("");
            return;
        }

        setSelectedPickupTime((prevSelected) => {
            const isExistingOption = pickupQuickTimeOptions.some((option) => option.apiValue === prevSelected);
            return isExistingOption ? prevSelected : pickupQuickTimeOptions[0].apiValue;
        });
    }, [pickupQuickTimeOptions]);

    const selectedPickupOption = useMemo(() => {
        if (!pickupQuickTimeOptions.length) return null;
        return pickupQuickTimeOptions.find((option) => option.apiValue === selectedPickupTime)
            || pickupQuickTimeOptions[0]
            || null;
    }, [pickupQuickTimeOptions, selectedPickupTime]);

    const basePickupOption = pickupQuickTimeOptions[0] || null;

    const finalDropoffValueText = useMemo(() => {
        const deliveryDurationMinutes = Number(delivery?.delivery_duration);
        if (!selectedPickupOption?.displayValue || !Number.isFinite(deliveryDurationMinutes)) return "";

        const pickupDateTime = parseDateValue(
            selectedPickupOption?.apiValue || selectedPickupOption?.displayValue
        );
        if (!pickupDateTime) return "";

        const dropoffDateTime = new Date(pickupDateTime.getTime() + (deliveryDurationMinutes * 60 * 1000));

        return formatDatetimeByLocale(dropoffDateTime, i18n, {
            fallback: "- - -",
            includeSeconds: false,
        });
    }, [
        delivery?.delivery_duration,
        i18n,
        selectedPickupOption?.apiValue,
        selectedPickupOption?.displayValue,
    ]);

    const pickupQuickSelectorOptions = useMemo(() => (
        pickupQuickTimeOptions.map((option) => ({
            ...option,
            value: option.apiValue,
        }))
    ), [pickupQuickTimeOptions]);

    const removeOrderFromNotifications = useCallback((reasonKey) => {
        if (!activeOrderDetails?.id) return;

        dispatch(addToFilteredNotificationOrders({
            [activeOrderDetails.id]: { reasonKey: reasonKey || "filtered" }
        }));
    }, [dispatch, activeOrderDetails?.id]);

    const handleOpenOrder = useCallback(() => {
        if (!activeOrderDetails?.id) return;

        const openOrder = () => {
            openOrderByOrderDetails({ orderDetails: activeOrderDetails });
        };

        if (window.location.pathname !== "/") {
            const currSP_str = new URLSearchParams(window.location.search).toString();
            navigate(`/${currSP_str ? "?" + currSP_str : ""}`, { replace: true });
            setTimeout(openOrder, 0);
        } else {
            openOrder();
        }
    }, [activeOrderDetails, openOrderByOrderDetails, navigate]);

    const showPrintingErrorToast = useCallback((error: unknown) => {
        const errorText = String(getCheckoutPrintErrorMessage({
            error,
            t,
        }));

        toast.error(
            <div>
                <h6 className="text-capitalize">
                    {t("printingFailed")}
                </h6>
                <div>{errorText}</div>
            </div>,
            {
                duration: 8000
            }
        );
    }, [t]);

    const handlePrintAcceptedOrderPaymentDetails = useCallback(async (acceptedOrderDetails: CashBoxOrderDetails) => {
        const {
            data: targetCheckoutData,
            errorCode: extractCheckoutErrorCode,
        } = extractTargetOrLastCheckout({
            receiptDetails: acceptedOrderDetails,
        });

        if (!targetCheckoutData) {
            if (extractCheckoutErrorCode === "missing_paid_checkout") {
                return;
            }

            showPrintingErrorToast({ message: t("checkoutPrintDataUnavailable") });
            return;
        }

        const {
            orderId,
            checkoutDetails,
            checkoutItems,
            checkoutCustomItems,
            checkoutManualItems,
        } = targetCheckoutData;

        if (printMethod === "browser") {
            // printDataInCurrentWindow("transaction_receipt_data");
            return;
        }

        // if (printingRequestIsPending) {
        //     return;
        // }

        // setPrintingRequestIsPending(true);

        const printResult = await printCheckoutReceipt({
            printMethod,
            checkoutId: checkoutDetails?.checkout_id,
            allowBluetoothDeviceSelection: false,
            allowUsbDeviceSelection: false,
            printPayload: {
                printerSize,
                restaurantName: restaurantDetails?.restaurant_name,
                restaurantLogo: restaurantDetails?.restaurant_logo,
                waiterName: restaurantDetails?.waiter_name ?? restaurantDetails?.waiter_email,
                currency: restaurantDetails?.currency,
                orderId,
                checkoutUpdatedAt: checkoutDetails.updated_at,
                ordersPrice: checkoutDetails.orders_price,
                discountPrice: checkoutDetails.discount_price,
                tipPrice: checkoutDetails.tip_price,
                paidPrice: checkoutDetails.paid_price,
                taxes: checkoutDetails.taxes,
                qrBase64: checkoutDetails.kasse_service?.qr_base64,
                qrSignatureText: checkoutDetails.kasse_service?.signature_text,
                items: checkoutItems,
                customItems: checkoutCustomItems,
                manualItems: checkoutManualItems,
            },
        });

        if (!printResult?.success && !printResult?.skipped) {
            showPrintingErrorToast(printResult?.error);
        }

        // setPrintingRequestIsPending(false);
    }, [
        // activeOrderDetails,
        printerSize,
        restaurantDetails,
        showPrintingErrorToast,
        t,
        printMethod,
    ]);

    const handleAcceptOrder = useCallback(async () => {
        if (!activeOrderDetails?.id || actionLoading) return;

        const pickupTimeToSubmit = selectedPickupOption?.apiValue || "";
        const receiverAddressDetails = deliveryAddressDetails;

        if (isPickup) {
            // if (!pickupEmail) {
            //     toast.error(t("emailIsRequired"));
            //     return;
            // }

            // if (!pickupPhoneNumber) {
            //     toast.error(t("phoneNumberIsRequired"));
            //     return;
            // }
            if (!pickupEmail && !pickupPhoneNumber) {
                toast.error(t("emailOrPhoneIsRequired"));
                return;
            }
        } else if (!deliveryData) {
            toast.error(t("deliveryDetails"));
            return;
        }

        setActionLoading("accept");

        const {
            respData,
        } = (await acceptDeliveryOrPickupOrder({
            orderId: activeOrderDetails.id,
            pickupDateTime: pickupTimeToSubmit,
            isPickup,
            email: pickupEmail,
            countrySymbol: pickupDialCodeSymbol,
            phoneNumber: pickupPhoneNumber,
            ...(!isPickup && {
                deliveryData: {
                    receiverLatitude: deliveryData?.receiver_latitude,
                    receiverLongitude: deliveryData?.receiver_longitude,
                    fullAddress: receiverAddressDetails?.full_address,
                    country: receiverAddressDetails?.country,
                    state: receiverAddressDetails?.state,
                    city: receiverAddressDetails?.city,
                    locality: receiverAddressDetails?.locality,
                    street: receiverAddressDetails?.street,
                    houseNumber: receiverAddressDetails?.house_number,
                    postalCode: receiverAddressDetails?.postal_code,
                    doorNumber: receiverAddressDetails?.apartment_door,
                    apartmentEntrance: receiverAddressDetails?.entrance,
                    floor: receiverAddressDetails?.floor,
                    extraDetails: receiverAddressDetails?.address_extra_details,
                    description: deliveryData?.description,
                    receiverFullName: deliveryData?.receiver_full_name,
                },
            }),
            afterSuccess: () => {
                removeOrderFromNotifications("accepted");
                toast.success(t("acceptOrder"));
                handleClose();
            },
        })) || {};

        if (respData && shouldAutoPrint) {
            await handlePrintAcceptedOrderPaymentDetails(respData);
        }

        setActionLoading("");
    }, [
        deliveryAddressDetails,
        acceptDeliveryOrPickupOrder,
        activeOrderDetails,
        actionLoading,
        deliveryData,
        handlePrintAcceptedOrderPaymentDetails,
        handleClose,
        isPickup,
        shouldAutoPrint,
        pickupDialCodeSymbol,
        pickupEmail,
        pickupPhoneNumber,
        removeOrderFromNotifications,
        selectedPickupOption?.apiValue,
        t,
    ]);

    const handleRejectOrder = useCallback(async () => {
        if (!activeOrderDetails?.id || actionLoading) return;

        setActionLoading("reject");

        // await cancelOrderWithConfirm({
        //     orderId: activeOrderDetails.id,
        //     afterConfirm: () => {
        //         setShowRejectLoader(true);
        //     },
        //     afterSuccess: () => {
        //         removeOrderFromNotifications("rejected");
        //         toast.success(t("rejectOrder"));
        //         handleClose();
        //     },
        //     afterComplete: () => {
        //         setShowRejectLoader(false);
        //     },
        // });
        await cancelOrder({
            orderId: activeOrderDetails.id,
            afterSuccess: () => {
                removeOrderFromNotifications("rejected");
                toast.success(t("rejectOrder"));
                handleClose();
            },
        });

        setActionLoading("");
    }, [cancelOrder, actionLoading, activeOrderDetails?.id, handleClose, removeOrderFromNotifications, t]);

    if (!activeOrderDetails) return null;

    return (
        <CustomModal
            centered
            show={true}
            onHide={handleClose}
            backdrop={backdrop}
            keyboard={false}
            enforceFocus={enforceFocus}
            className="incoming-order-notification-modal"
            dialogClassName="incoming-order-notification-modal-dialog"
            size="lg"
            style={modalStyle}
            disableAutoStacking={true}
        >
            <Modal.Header closeButton>
                <Modal.Title className="fw-normal">
                    {t("incomingOrderNotificationTitle")}
                </Modal.Title>
            </Modal.Header>

            <Modal.Body>
                <p className="text-secondary mb-3">
                    {t("incomingOrderNotificationDescription")}
                </p>

                <div className="order-main-info-box-wrapper border rounded overflow-hidden">
                    <div className="w-100 text-start p-3">
                        <div className="d-flex justify-content-between align-items-center">
                            <strong>{t("orderId")}: {activeOrderDetails?.id ?? "- - -"}</strong>
                            <small className="text-muted">{createdAtText}</small>
                        </div>

                        <div className="mt-2 small">
                            <div>
                                <strong>
                                    {t("totalPrice")}:
                                </strong>
                                <span className="fs-6 ms-3">
                                    {orderTotalText}
                                    {tipAmount > 0 && (
                                        <small className="text-muted ms-2 pb-1 px-1 fw-semibold">
                                            +{tipAmount.toFixed(2)} {t("tip")}
                                        </small>
                                    )}
                                </span>
                            </div>
                            <div>
                                <strong>
                                    {t("orderType")}:
                                </strong>
                                <span className="fs-6 ms-3">
                                    {orderTypeText}
                                </span>
                            </div>
                            {/* <div><strong>{t("orderStatus")}:</strong> {orderStatusText}</div> */}
                        </div>
                    </div>
                    <button
                        type="button"
                        className="open-inbackground-btn btn w-100 rounded-0 border-start-0 border-end-0 border-bottom-0 text-capitalize"
                        onClick={handleOpenOrder}
                        disabled={!!actionLoading}
                    >
                        <Trans i18nKey="openOrderInBackground">
                            <strong>Open</strong><small className="fw-bold">(in background)</small>
                        </Trans>
                    </button>
                </div>

                <div className="mt-3">
                    <div className="fw-bold mb-2">{t("items")}</div>
                    {staticItems.length > 0 ? (
                        <div>
                            {staticItems.map((item) => (
                                <div key={item.key} className="d-flex align-items-center py-2">
                                    <div className="me-2 flex-shrink-0">
                                        {item.image ? (
                                            <img
                                                src={item.image}
                                                width={44}
                                                height={44}
                                                className="food-item-image"
                                                alt={item.title || ""}
                                            />
                                        ) : (
                                            <div
                                                className="d-flex align-items-center justify-content-center bg-light text-muted"
                                                style={{ width: "44px", height: "44px", borderRadius: "8px", fontSize: "12px" }}
                                            >
                                                {item.count || 1}x
                                            </div>
                                        )}
                                    </div>

                                    <div className="flex-grow-1">
                                        <div className="small fw-semibold">{item.title}</div>
                                        <div className="small text-muted">
                                            {t("count")}: {item.count || 1}
                                        </div>
                                    </div>

                                    <div className="small text-muted ms-2">
                                        {currency} {(Number(item.unitPrice) || 0).toFixed(2)}
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="text-muted small">
                            {t("noAddedItems")}
                        </div>
                    )}
                </div>

                {!isPickup && deliveryData && (
                    <div className="mt-3">
                        <ReadonlyDeliverySummary
                            addressData={deliveryAddressDetails}
                            description={deliveryData?.description ?? ""}
                            fullName={deliveryData?.receiver_full_name ?? ""}
                            email={deliveryData?.receiver_email ?? ""}
                            phone={{
                                dialCode: deliveryData?.receiver_phone?.country_symbol ?? "",
                                phoneNumber: deliveryData?.receiver_phone?.number ?? "",
                            }}
                            pickupTime={pickup_datetime}
                        />
                    </div>
                )}

                {isPickup && (
                    <div className="mt-3">
                        <ReadonlyPickupSummary
                            email={pickupEmail}
                            phone={{
                                dialCode: pickupDialCodeSymbol,
                                phoneNumber: pickupPhoneNumber,
                            }}
                        />
                    </div>
                )}

                {(!isPickup && !deliveryData && detailsLoading) && (
                    <div className="small text-muted mt-3">
                        {t("loading")}...
                    </div>
                )}

                <div className="mt-3">
                    <div className="fw-bold mb-2">{t("pickupTime")}</div>
                    <PickupQuickTimeSelector
                        options={pickupQuickSelectorOptions}
                        selectedValue={selectedPickupTime}
                        onSelect={(option: PickupQuickTimeSelectorOption) => {
                            setSelectedPickupTime(String(option?.apiValue ?? option?.value ?? ""));
                        }}
                        disabled={!!actionLoading}
                        baseValueText={basePickupOption?.displayValue}
                        finalValueText={selectedPickupOption?.displayValue}
                        finalDropoffValueText={finalDropoffValueText}
                        labelsPosition="bottom"
                        compact={true}
                        optionsClassName="mt-2"
                    />
                </div>
            </Modal.Body>

            <Modal.Footer className="d-flex justify-content-between align-items-center">
                <button
                    type="button"
                    className="btn btn-outline-secondary text-capitalize"
                    onClick={handleClose}
                >
                    {t("close")}
                </button>
                <div className="d-flex align-items-center gap-2">
                    {orderNeedsAcceptance && canRejectOrder && (
                        <button
                            type="button"
                            className="btn btn-outline-danger text-capitalize"
                            disabled={!!actionLoading}
                            onClick={handleRejectOrder}
                        >
                            {actionLoading === "reject" ? `${t("rejectOrder")}...` : t("rejectOrder")}
                        </button>
                    )}
                    {orderNeedsAcceptance && (
                        <button
                            type="button"
                            className="btn btn-dark text-capitalize"
                            disabled={!!actionLoading}
                            onClick={handleAcceptOrder}
                        >
                            {actionLoading === "accept" ? `${t("acceptOrder")}...` : t("acceptOrder")}
                        </button>
                    )}
                </div>
            </Modal.Footer>
        </CustomModal>
    );
};

export default OrderSocketNotificationModal;
